import { generateClient } from 'aws-amplify/api'
import { generateGetPresignedUrl, generatePresignedUrlForPut } from '@/graphql/mutations'
import { getApiMessage, isApiSuccess } from '@/mixins/Extras/ApiResponse'
import { buildPublicS3Url } from '@/mixins/Organization/OrganizationStorage.js'
import { parseAwsJson, unwrapApiPayload } from '@/utils/graphql'

const client = generateClient()

const ORG_DETAILS_URL =
  process.env.VUE_APP_ORG_DETAILS_API_URL ||
  'https://7xcn7ul6cl.execute-api.ap-south-1.amazonaws.com/prod/org_details'

const PRESIGNED_READ_URL =
  process.env.VUE_APP_PRESIGNED_READ_API_URL ||
  'https://7xcn7ul6cl.execute-api.ap-south-1.amazonaws.com/prod/generateGetPresignedUrl'

const READ_FILE_TYPE_HINTS = ['GET', 'get_object', 'READ', 'read', 'download']

function resolvePresignedUrl(payload) {
  if (!payload || typeof payload !== 'object') return null

  const candidates = [
    payload.presigned_url,
    payload.presignedUrl,
    payload.url,
    payload.download_url,
    payload.downloadUrl,
    payload.read_url,
    payload.readUrl,
    payload.data?.presigned_url,
    payload.data?.presignedUrl,
    payload.data?.url,
    payload.data?.download_url,
    payload.data?.downloadUrl,
    payload.data?.read_url,
    payload.data?.readUrl,
  ]

  const match = candidates.find((value) => typeof value === 'string' && value.trim())
  return match?.trim() || null
}

async function fetchPresignedGetViaGraphql(s3Key) {
  const result = await client.graphql({
    query: generateGetPresignedUrl,
    variables: {
      input: { s3_key: s3Key },
    },
  })

  const parsed = parseAwsJson(result.data?.generateGetPresignedUrl)
  if (!isApiSuccess(parsed)) return null

  const item = unwrapApiPayload(parsed)
  return resolvePresignedUrl(item) ?? resolvePresignedUrl(parsed)
}

async function fetchPresignedReadViaPutMutation(s3Key) {
  for (const fileType of READ_FILE_TYPE_HINTS) {
    try {
      const result = await client.graphql({
        query: generatePresignedUrlForPut,
        variables: {
          input: {
            s3_key: s3Key,
            file_type: fileType,
          },
        },
      })

      const parsed = parseAwsJson(result.data?.generatePresignedUrlForPut)
      if (parsed?.status !== 'SUCCESS') continue

      const url = resolvePresignedUrl(parsed)
      if (url) return url
    } catch {
      // try next file_type hint
    }
  }

  return null
}

async function fetchPresignedViaRest(s3Key) {
  const response = await fetch(PRESIGNED_READ_URL, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      command: 'generateGetPresignedUrl',
      s3_key: s3Key,
    }),
  })

  if (!response.ok) {
    throw new Error(`Presigned URL request failed (${response.status}).`)
  }

  const raw = await response.json()
  if (!isApiSuccess(raw)) {
    throw new Error(getApiMessage(raw, 'Could not load file.'))
  }

  const item = unwrapApiPayload(raw)
  return resolvePresignedUrl(item) ?? resolvePresignedUrl(raw)
}

/**
 * Fetches public org branding for the login/landing page.
 * @param {string} internalOrgId - URL slug (internal_org_id), e.g. "airindia"
 */
export async function getOrgDetailsForLandingPage(internalOrgId) {
  const slug = String(internalOrgId ?? '').trim()
  if (!slug) {
    throw new Error('Organisation link is invalid.')
  }

  const response = await fetch(ORG_DETAILS_URL, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      command: 'getOrgDetailsForLandingPage',
      org_id: slug,
    }),
  })

  if (!response.ok) {
    throw new Error(`Organisation lookup failed (${response.status}).`)
  }

  return response.json()
}

/**
 * Fetches a presigned GET URL for any S3 key (logo, knowledge base files, etc.).
 * @param {string} s3Key
 */
export async function getS3PresignedReadUrl(s3Key) {
  const key = String(s3Key ?? '').trim()
  if (!key) return null

  if (/^https?:\/\//i.test(key) || /^data:/i.test(key) || /^blob:/i.test(key)) {
    return key
  }

  const strategies = [
    () => fetchPresignedGetViaGraphql(key),
    () => fetchPresignedReadViaPutMutation(key),
    () => fetchPresignedViaRest(key),
  ]

  for (const strategy of strategies) {
    try {
      const url = await strategy()
      if (url) return url
    } catch (err) {
      console.log('S3 presigned read attempt failed', err)
    }
  }

  return buildPublicS3Url(key)
}

/** @deprecated alias — use getS3PresignedReadUrl */
export async function getOrgLogoPresignedReadUrl(s3Key) {
  return getS3PresignedReadUrl(s3Key)
}

function inferMimeFromS3Key(s3Key, kind) {
  if (kind === 'pdf') return 'application/pdf'
  if (kind === 'text') return 'text/plain'
  const lower = String(s3Key || '').toLowerCase()
  if (lower.endsWith('.pdf')) return 'application/pdf'
  if (lower.endsWith('.txt')) return 'text/plain'
  return 'application/octet-stream'
}

/**
 * Downloads S3 object bytes via presigned URL and returns a typed Blob for in-app preview.
 */
export async function fetchS3BlobViaPresigned(s3Key, kind = null) {
  const url = await getS3PresignedReadUrl(s3Key)
  if (!url) return null

  const response = await fetch(url)
  if (!response.ok) {
    throw new Error(`File fetch failed (${response.status}).`)
  }

  const rawBlob = await response.blob()
  const mime =
    inferMimeFromS3Key(s3Key, kind) ||
    rawBlob.type ||
    'application/octet-stream'
  const buffer = await rawBlob.arrayBuffer()
  return new Blob([buffer], { type: mime })
}

/**
 * Blob URL for iframe/embed preview. Caller must revoke with URL.revokeObjectURL.
 */
export async function createS3PreviewBlobUrl(s3Key, kind) {
  const blob = await fetchS3BlobViaPresigned(s3Key, kind)
  if (!blob?.size) return null
  return URL.createObjectURL(blob)
}

export async function fetchS3TextViaPresigned(s3Key) {
  const blob = await fetchS3BlobViaPresigned(s3Key, 'text')
  if (!blob) return null
  return blob.text()
}
